import React from 'react';
import { Popover, Button, type PopoverProps } from '@blueprintjs/core-modern';

export type FilterPopoverProps<T = React.HTMLProps<HTMLElement>> = Omit<PopoverProps<T>, 'children'> & {
  /** When set, renders a Button with this content as the popover trigger (string or React node, e.g. <strong>bold</strong> text). Otherwise `children` is the target. */
  activateText?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * Filter-specific popover based on Blueprint Popover.
 * Styling: arrow is removed. Pass `activateText` to use a button as the trigger, or pass `children` for a custom target.
 */
export const FilterPopover = <T extends React.HTMLProps<HTMLElement> = React.HTMLProps<HTMLElement>>(
  props: FilterPopoverProps<T>
): React.ReactElement => {
  const { modifiers, popoverClassName, activateText, children, ...rest } = props;
  const mergedModifiers = {
    ...modifiers,
    arrow: { ...modifiers?.arrow, enabled: false },
  };

  return (
    <Popover
      interactionKind="click"
      position="bottom-left"
      transitionDuration={0}
      autoFocus
      {...rest}
      modifiers={mergedModifiers}
      popoverClassName={popoverClassName ? `modern-filter-popover ${popoverClassName}` : 'modern-filter-popover'}
      renderTarget={
        activateText != null
          ? ({ isOpen, ref, ...targetProps }) => (
            <Button
              ref={ref as React.Ref<HTMLButtonElement>}
              {...targetProps}
              endIcon={isOpen ? 'chevron-up' : 'chevron-down'}
              className="modern-filter-popover-button"
              variant="minimal"
            >
              {activateText}
            </Button>
          )
          : undefined
      }
    >
      {activateText != null ? undefined : children}
    </Popover>
  );
};


FilterPopover.displayName = 'FilterPopover';
